import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { constants, NotificationManager, PaymentsFormsApi } from '@atlas/core';
import { FieldType } from '@atlas/payments';
import { DefaultPopup, Loader } from '@atlas/ui';

import useFormatter from 'hooks/useFormatter';
import { usePixPopupPostHog } from 'hooks/usePostHog';
import PaymentsForm from 'src/common/Notifications/Popups/PaymentsFormPopup/PaymentsForm';

import ExpirationCountdownTimer from './ExpirationCountdownTimer';
import PixDepositResultQRCard from './PixDepositResultQRCard';
import { generateNewPixCode } from './utils';

const PixDepositResultPopup = ({
  data: {
    amount,
    createTime,
    formId,
    interpolationValues = {},
    paymentMethod,
    paymentOptions,
    pixCode,
    publicPaymentId,
  },
  id,
}) => {
  const { currencyFormatter } = useFormatter();
  const formattedAmount = currencyFormatter.format(amount);
  const {
    handleCopyCode,
    handleGenerateNewCode: trackGenerateNewCode,
    handleManualClose,
  } = usePixPopupPostHog();

  const [formConfig, setFormConfig] = useState(null);
  const [expirationCountdown, setExpirationCountdown] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const handleManualCloseClick = useCallback(() => {
    handleManualClose();
    handleClose();
  }, [handleClose, handleManualClose]);

  const showError = e => {
    NotificationManager.display({
      data: {
        text: e.message,
        title: 'payments_form_error_title',
      },
      type: 'paymentFormError',
    });
  };

  useEffect(() => {
    PaymentsFormsApi.getPaymentsForms(formId)
      .then(config => {
        const expirationField = config.fields?.find(
          f => f.fieldType === FieldType.EXPIRATION_COUNTDOWN,
        );
        const countdown = expirationField ? Number(expirationField.value) : null;

        if (!countdown) {
          handleClose();
          NotificationManager.display({
            data: {
              amount,
              formId,
              paymentMethod,
              publicPaymentId,
              type: constants.PAYMENT_OPERATION.DEPOSIT,
            },
            type: 'paymentsForm',
          });
          return;
        }

        setFormConfig(config);
        setExpirationCountdown(countdown);
      })
      .catch(e => {
        handleClose();
        showError(e);
      });
  }, [amount, formId, handleClose, paymentMethod, publicPaymentId]);

  const handleExpire = useCallback(() => {
    setIsExpired(true);
  }, []);

  const handleCopy = useCallback(
    value => {
      handleCopyCode();
      navigator.clipboard
        .writeText(value)
        .then(() => {
          setIsCopied(true);
        })
        .catch(() => {});
    },
    [handleCopyCode],
  );

  const handleGenerateNewCode = useCallback(async () => {
    trackGenerateNewCode();
    setIsLoading(true);
    try {
      await generateNewPixCode({
        amount,
        createTime,
        expirationCountdown,
        formId,
        id,
        interpolationValues,
        paymentMethod,
        paymentOptions,
        publicPaymentId,
      });
    } catch (e) {
      setIsLoading(false);
      showError(e);
    }
  }, [
    amount,
    createTime,
    expirationCountdown,
    formId,
    id,
    interpolationValues,
    paymentMethod,
    paymentOptions,
    publicPaymentId,
    trackGenerateNewCode,
  ]);

  const formInterpolationValues = useMemo(
    () => ({
      externalURL: pixCode,
      externalUrl: pixCode,
      redirectUrl: pixCode,
      ...interpolationValues,
    }),
    [interpolationValues, pixCode],
  );

  const expiredAt = useMemo(() => {
    if (!expirationCountdown) return null;

    return Date.now() + expirationCountdown * 1000;
  }, [expirationCountdown]);

  const renderCustomField = ({ fieldType, id: fieldId, mappingKey, value }) => {
    if (fieldType === FieldType.COPY_BUTTON) {
      return null;
    }

    if (fieldType === FieldType.EXPIRATION_COUNTDOWN) {
      return (
        <ExpirationCountdownTimer
          expirationCountdown={expirationCountdown}
          expiredAt={expiredAt}
          isLoading={isLoading}
          key={mappingKey || fieldId}
          onExpire={handleExpire}
          onGenerateNewCode={handleGenerateNewCode}
        />
      );
    }

    if (fieldType === FieldType.QR_CODE) {
      // custom copy button is rendered inside PixDepositResultQRCard
      const hasCopyButton = formConfig?.fields?.some(f => f.fieldType === FieldType.COPY_BUTTON);
      const qrCode = value ? String(value) : pixCode;

      return (
        <PixDepositResultQRCard
          formattedAmount={formattedAmount}
          hasCopyButton={hasCopyButton}
          isCopied={isCopied}
          isExpired={isExpired}
          key={mappingKey || fieldId}
          onCopy={() => handleCopy(qrCode)}
          pixCode={qrCode}
        />
      );
    }

    return false;
  };

  return (
    <DefaultPopup
      className="pix-deposit-result"
      headerTitle="pix_header_title"
      headerTitlePosition="left"
      onClose={handleManualCloseClick}
    >
      {formConfig ? (
        <div className="payments-form-wrapper pix-deposit-result__form">
          <PaymentsForm
            formConfig={formConfig}
            interpolationValues={formInterpolationValues}
            renderCustomField={renderCustomField}
          />
        </div>
      ) : (
        <Loader />
      )}
    </DefaultPopup>
  );
};

PixDepositResultPopup.propTypes = {
  data: PropTypes.shape({
    amount: PropTypes.number.isRequired,
    createTime: PropTypes.number,
    formId: PropTypes.number.isRequired,
    interpolationValues: PropTypes.object,
    paymentMethod: PropTypes.object.isRequired,
    paymentOptions: PropTypes.object,
    pixCode: PropTypes.string,
    publicPaymentId: PropTypes.string.isRequired,
  }).isRequired,
  id: PropTypes.string.isRequired,
};

export default PixDepositResultPopup;
